import './SortingRowForm.scss';
import { faBars, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Draggable, DraggableProvided, DraggableStateSnapshot } from '@hello-pangea/dnd';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import ClassNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import ReactDOM from 'react-dom';
import { ISelectOption } from '../../../../../../../../design/inputs/select/ISelectOption';
import { Select } from '../../../../../../../../design/inputs/select/Select';
import { S_FieldLabel } from '../../../../../../../../design/labels/fieldLabel/S_FIeldLabel';
import { TrashIcon } from '../../../../../../../../design/static/icons/TrashIcon';
import { SortingDirection } from '../../../../../../../models/sortingDirection.model';
import { ISorting, SortingBy } from '../../../imageFilters.model';

interface IProps {
  order: number;
  sorting: ISorting;
  options: ISelectOption<SortingBy>[];
  onChange(sorting: ISorting): void;
  onDelete(order: number): void;
}

export const SortingRowForm = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();

  const handleByChange = (selected: SortingBy | null): void => {
    props.onChange({ ...props.sorting, by: selected ?? undefined });
  };

  const handleDelete = (): void => {
    props.onDelete(props.sorting.order);
  };

  const handleDirectionChange = (direction: SortingDirection): void => {
    if (props.sorting.direction === direction) {
      return;
    }
    props.onChange({ ...props.sorting, direction });
  };

  const renderItem = (provided: DraggableProvided, snapshot: DraggableStateSnapshot): ReactElement => {
    const usePortal: boolean = snapshot.isDragging;

    const item = (
      <div
        className='image-filter-sorting'
        {...provided.draggableProps}
        ref={provided.innerRef}
      >
        <div className='wrapper'>
          <S_FieldLabel>{t(props.order === 0 ? 'sort_by' : 'then_by')}</S_FieldLabel>
          <div className='input-wrapper'>
            <div
              className='drag-handle'
              {...provided.dragHandleProps}
            >
              <FontAwesomeIcon icon={faBars} />
            </div>
            <Select
              value={props.options.find((x) => x.value === props.sorting.by)}
              onChange={(_, v) => {
                handleByChange(v.value);
              }}
              className='field-select'
              options={props.options}
              getOptionDisabled={(option) => option.disabled ?? false}
            />
            <div className='order-controls'>
              <div
                title={t('ascending')}
                className={ClassNames({ 'order-control': true, up: true, active: props.sorting.direction === SortingDirection.ASC })}
                onClick={(): void => {
                  handleDirectionChange(SortingDirection.ASC);
                }}
              >
                <FontAwesomeIcon icon={faSortUp} />
              </div>
              <div
                title={t('descending')}
                className={ClassNames({ 'order-control': true, down: true, active: props.sorting.direction === SortingDirection.DESC })}
                onClick={(): void => {
                  handleDirectionChange(SortingDirection.DESC);
                }}
              >
                <FontAwesomeIcon icon={faSortDown} />
              </div>
            </div>
            {props.order > 0 && (
              <IconButton
                className='trash'
                onClick={handleDelete}
                title={t('remove')}
              >
                <TrashIcon />
              </IconButton>
            )}
          </div>
        </div>
      </div>
    );

    if (!usePortal) {
      return item;
    }

    // if dragging - put the item in a portal
    return ReactDOM.createPortal(item, document.body);
  };

  return (
    <Draggable
      key={`sorting-key-${props.sorting.order}`}
      draggableId={`sorting-key-${props.sorting.order}`}
      index={props.sorting.order}
    >
      {(provided, draggableSnapshot): ReactElement => renderItem(provided, draggableSnapshot)}
    </Draggable>
  );
});
